import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken, clearToken } from './App';
import Layout from './Layout';
import { Ico } from './icons';


function Employees() {
    const { t } = useTranslation();
    const [employees, setEmployees] = useState([]);
    const [includeInactive, setIncludeInactive] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ full_name: '', email: '', nfc_uid: '' });
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [kioskKey, setKioskKey] = useState('');
    const [kioskKeyVisible, setKioskKeyVisible] = useState(false);
    const [kioskKeyLoading, setKioskKeyLoading] = useState(false);
    const [kioskCopied, setKioskCopied] = useState(false);
    const [pinModal, setPinModal] = useState(null);
    const [pinValue, setPinValue] = useState('');
    const [pinError, setPinError] = useState('');
    const [pinSubmitting, setPinSubmitting] = useState(false);
    const navigate = useNavigate();

    const authHeaders = () => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
    });

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        fetch(`/api/employees?include_inactive=${includeInactive ? 'true' : 'false'}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` },
        })
            .then((res) => {
                if (res.status === 401) {
                    clearToken();
                    localStorage.removeItem('nexus_user');
                    navigate('/login', { replace: true });
                    throw new Error(t('common.session_expired'));
                }
                if (!res.ok) throw new Error(t('common.http_error', { status: res.status }));
                return res.json();
            })
            .then((data) => setEmployees(data.employees || []))
            .catch((err) => {
                if (err.message !== t('common.session_expired')) setError(err.message);
            })
            .finally(() => setLoading(false));
    }, [includeInactive, navigate, t]);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setEditing(null);
        setForm({ full_name: '', email: '', nfc_uid: '' });
        setFormError('');
        setModalOpen(true);
    };

    const openEdit = (emp) => {
        setEditing(emp);
        setForm({
            full_name: emp.full_name || '',
            email: emp.email || '',
            nfc_uid: emp.nfc_uid || '',
        });
        setFormError('');
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditing(null);
        setForm({ full_name: '', email: '', nfc_uid: '' });
        setFormError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        setSubmitting(true);
        try {
            const payload = {
                full_name: form.full_name.trim(),
                email: form.email.trim(),
                nfc_uid: form.nfc_uid.trim(),
            };
            if (!payload.full_name) {
                setFormError(t('employees.err_name_required'));
                return;
            }
            if (!payload.nfc_uid) {
                setFormError(t('employees.err_nfc_required'));
                return;
            }

            const url = editing
                ? `/api/employees/${editing.id}`
                : '/api/employees';
            const method = editing ? 'PATCH' : 'POST';

            const res = await fetch(url, {
                method,
                headers: authHeaders(),
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) {
                setFormError(data.error || t('common.http_error', { status: res.status }));
                return;
            }
            setSuccess(editing ? t('employees.success_updated') : t('employees.success_created'));
            closeModal();
            load();
        } catch (err) {
            setFormError(t('common.network_error', { message: err.message }));
        } finally {
            setSubmitting(false);
        }
    };

    const toggleActive = async (emp) => {
        if (!window.confirm(
            emp.active
                ? t('employees.confirm_deactivate', { name: emp.full_name })
                : t('employees.confirm_reactivate', { name: emp.full_name })
        )) return;
        setError('');
        setSuccess('');
        try {
            const res = await fetch(`/api/employees/${emp.id}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({ active: !emp.active }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || t('common.http_error', { status: res.status }));
                return;
            }
            setSuccess(
                emp.active
                    ? t('employees.success_deactivated', { name: emp.full_name })
                    : t('employees.success_reactivated', { name: emp.full_name })
            );
            load();
        } catch (err) {
            setError(t('common.network_error', { message: err.message }));
        }
    };

    const initials = (name) => {
        const p = (name || '?').trim().split(/\s+/).filter(Boolean);
        if (p.length === 0) return '?';
        if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
        return (p[0][0] + p[p.length - 1][0]).toUpperCase();
    };

    const activeCount = employees.filter((e) => e.active).length;

    const revealKioskKey = async () => {
        if (kioskKeyVisible) {
            setKioskKeyVisible(false);
            return;
        }
        setKioskKeyLoading(true);
        try {
            const res = await fetch('/api/kiosk/key', {
                headers: { 'Authorization': `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || t('employees.kiosk_key_err'));
                return;
            }
            setKioskKey(data.kiosk_key || '');
            setKioskKeyVisible(true);
            setKioskCopied(false);
        } catch (err) {
            setError(t('common.network_error', { message: err.message }));
        } finally {
            setKioskKeyLoading(false);
        }
    };

    const copyKioskKey = async () => {
        try {
            await navigator.clipboard.writeText(kioskKey);
            setKioskCopied(true);
            setTimeout(() => setKioskCopied(false), 2200);
        } catch (e) {
            setKioskCopied(false);
        }
    };

    const openPinModal = (emp) => {
        setPinModal(emp);
        setPinValue('');
        setPinError('');
    };

    const closePinModal = () => {
        setPinModal(null);
        setPinValue('');
        setPinError('');
    };

    const submitPin = async (e) => {
        e.preventDefault();
        if (!pinModal) return;
        setPinError('');
        if (!/^\d{4,8}$/.test(pinValue.trim())) {
            setPinError(t('employees.pin_format'));
            return;
        }
        setPinSubmitting(true);
        try {
            const res = await fetch(`/api/employees/${pinModal.id}/pin`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ pin_code: pinValue.trim() }),
            });
            const data = await res.json();
            if (!res.ok) {
                setPinError(data.error || t('common.http_error', { status: res.status }));
                return;
            }
            setSuccess(t('employees.pin_success', { name: pinModal.full_name }));
            closePinModal();
            load();
        } catch (err) {
            setPinError(t('common.network_error', { message: err.message }));
        } finally {
            setPinSubmitting(false);
        }
    };

    const removePin = async () => {
        if (!pinModal) return;
        if (!window.confirm(t('employees.pin_confirm_remove', { name: pinModal.full_name }))) return;
        setPinSubmitting(true);
        try {
            const res = await fetch(`/api/employees/${pinModal.id}/pin`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (!res.ok) {
                setPinError(data.error || t('common.http_error', { status: res.status }));
                return;
            }
            setSuccess(t('employees.pin_removed', { name: pinModal.full_name }));
            closePinModal();
            load();
        } catch (err) {
            setPinError(t('common.network_error', { message: err.message }));
        } finally {
            setPinSubmitting(false);
        }
    };

    const kioskUrl = `${window.location.origin}/terminal-de-fichaje`;

    return (
        <Layout title={t('common.employees')} subtitle={t('employees.subtitle')}>
            <section className="card kiosk-terminal-card">
                <div className="card-head">
                    <div>
                        <div className="card-title">
                            <Ico name="nfc" size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                            {t('employees.kiosk_title')}
                        </div>
                        <div className="card-sub">{t('employees.kiosk_desc')}</div>
                    </div>
                    <div className="card-tools">
                        <a href="/terminal-de-fichaje" target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                            <Ico name="arrowRight" size={14} />
                            {t('employees.kiosk_open')}
                        </a>
                        <button onClick={revealKioskKey} className="btn btn-primary btn-sm" disabled={kioskKeyLoading}>
                            {kioskKeyLoading
                                ? <Ico name="refresh" size={14} className="spin" />
                                : <Ico name="key" size={14} />}
                            {kioskKeyVisible ? t('employees.kiosk_key_hide') : t('employees.kiosk_key_btn')}
                        </button>
                    </div>
                </div>
                {kioskKeyVisible && (
                    <div className="kiosk-key-reveal">
                        <div className="kiosk-key-row">
                            <code className="kiosk-key-value">{kioskKey}</code>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={copyKioskKey}>
                                <Ico name={kioskCopied ? 'check' : 'file'} size={14} />
                                {kioskCopied ? t('employees.kiosk_key_copied') : t('employees.kiosk_key_copy')}
                            </button>
                        </div>
                        <div className="kiosk-url-row">
                            <span className="muted">{t('employees.kiosk_url')}:</span>
                            <code>{kioskUrl}</code>
                        </div>
                        <div className="hint">{t('employees.kiosk_key_hint')}</div>
                    </div>
                )}
            </section>

            <section className="card">
                <div className="card-head">
                    <div>
                        <div className="card-title">
                            {includeInactive
                                ? t('employees.list_title_all')
                                : t('employees.list_title_active')}
                        </div>
                        <div className="card-sub">
                            <span className="card-count">{employees.length}</span>
                            {' '}
                            <span className="muted">· {activeCount} {t('common.active').toLowerCase()}</span>
                        </div>
                    </div>
                    <div className="card-tools">
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={includeInactive}
                                onChange={(e) => setIncludeInactive(e.target.checked)}
                            />
                            <span className="switch-track"><span className="switch-dot" /></span>
                            <span className="switch-label">{t('employees.show_inactive')}</span>
                        </label>
                        <button onClick={openCreate} className="btn btn-primary">
                            <Ico name="plus" size={16} />
                            {t('employees.new_employee_plain', 'Nuevo empleado')}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-error" role="alert">
                        <Ico name="alert" size={17} />
                        <span>{error}</span>
                    </div>
                )}
                {success && (
                    <div className="alert alert-success" role="status">
                        <Ico name="check" size={17} />
                        <span>{success}</span>
                    </div>
                )}

                {loading ? (
                    <div className="skel-rows">
                        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="skel skel-row" />)}
                    </div>
                ) : employees.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-ico"><Ico name="users" size={27} /></div>
                        <div className="empty-title">{t('employees.empty_title', 'Sin empleados')}</div>
                        <div className="empty-sub">{t('employees.no_employees')}</div>
                    </div>
                ) : (
                    <div className="table-wrap">
                        <table className="nx-table">
                            <thead>
                                <tr>
                                    <th>{t('employees.table_name')}</th>
                                    <th>{t('employees.table_nfc')}</th>
                                    <th>{t('employees.table_status')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('employees.table_actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employees.map((emp) => (
                                    <tr key={emp.id} className={!emp.active ? 'row-inactive' : ''}>
                                        <td>
                                            <div className="emp-cell">
                                                <div className="avatar sm">{initials(emp.full_name)}</div>
                                                <div className="emp-meta">
                                                    <div className="emp-name">{emp.full_name}</div>
                                                    {emp.email && <div className="emp-mail">{emp.email}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            {emp.nfc_uid
                                                ? <span className="chip-code"><Ico name="fingerprint" size={11} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />{emp.nfc_uid}</span>
                                                : <span className="muted">—</span>}
                                            {emp.pin_set && (
                                                <span className="chip-code pin" title={t('employees.pin_badge')}>
                                                    <Ico name="keypad" size={11} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />
                                                    {t('employees.pin_badge')}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {emp.active
                                                ? <span className="badge badge-success"><span className="dot pulse" />{t('common.active')}</span>
                                                : <span className="badge badge-muted"><span className="dot" />{t('common.inactive')}</span>}
                                        </td>
                                        <td className="actions-cell" style={{ textAlign: 'right' }}>
                                            <button
                                                onClick={() => openPinModal(emp)}
                                                className={`btn-icon${emp.pin_set ? ' accent' : ''}`}
                                                title={t('employees.pin_set_btn')}
                                                aria-label={t('employees.pin_set_btn')}
                                            >
                                                <Ico name="keypad" size={15} />
                                            </button>
                                            <button
                                                onClick={() => openEdit(emp)}
                                                className="btn-icon"
                                                title={t('employees.edit_btn')}
                                                aria-label={t('employees.edit_btn')}
                                            >
                                                <Ico name="pencil" size={15} />
                                            </button>
                                            <button
                                                onClick={() => toggleActive(emp)}
                                                className={`btn-icon${emp.active ? '' : ' danger'}`}
                                                title={emp.active ? t('employees.deactivate_btn') : t('employees.reactivate_btn')}
                                                aria-label={emp.active ? t('employees.deactivate_btn') : t('employees.reactivate_btn')}
                                            >
                                                <Ico name={emp.active ? 'pause' : 'play'} size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {modalOpen && (
                <div className="modal-backdrop" onClick={closeModal}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-head">
                            <div className="modal-ico">
                                <Ico name={editing ? 'pencil' : 'plus'} size={18} />
                            </div>
                            <div className="modal-title">
                                {editing ? t('employees.modal_edit_title') : t('employees.modal_create_title')}
                            </div>
                            <button type="button" className="btn-icon modal-x" onClick={closeModal} aria-label={t('common.cancel')}>
                                <Ico name="close" size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="field">
                                <label className="field-label" htmlFor="emp-full-name">
                                    {t('employees.field_full_name')}
                                </label>
                                <div className="control">
                                    <span className="control-ico"><Ico name="user" size={17} /></span>
                                    <input
                                        id="emp-full-name"
                                        className="input pad-ico"
                                        type="text"
                                        value={form.full_name}
                                        onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                        placeholder={t('employees.field_full_name_ph')}
                                        required
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className="field">
                                <label className="field-label" htmlFor="emp-email">
                                    {t('employees.field_email')}
                                </label>
                                <div className="control">
                                    <span className="control-ico"><Ico name="mail" size={17} /></span>
                                    <input
                                        id="emp-email"
                                        className="input pad-ico"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        placeholder={t('employees.field_email_ph')}
                                    />
                                </div>
                            </div>

                            <div className="field">
                                <label className="field-label" htmlFor="emp-nfc">
                                    {t('employees.field_nfc')}
                                </label>
                                <div className="control">
                                    <span className="control-ico"><Ico name="fingerprint" size={17} /></span>
                                    <input
                                        id="emp-nfc"
                                        className="input pad-ico"
                                        type="text"
                                        value={form.nfc_uid}
                                        onChange={(e) => setForm({ ...form, nfc_uid: e.target.value })}
                                        placeholder={t('employees.field_nfc_ph')}
                                        required
                                    />
                                </div>
                            </div>

                            {formError && (
                                <div className="alert alert-error" role="alert">
                                    <Ico name="alert" size={17} />
                                    <span>{formError}</span>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button type="button" onClick={closeModal} className="btn btn-ghost">
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" disabled={submitting} className="btn btn-primary">
                                    {submitting
                                        ? <><Ico name="refresh" size={15} />{t('common.saving')}</>
                                        : <><Ico name={editing ? 'check' : 'plus'} size={15} />{editing ? t('employees.submit_edit') : t('employees.submit_create')}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {pinModal && (
                <div className="modal-backdrop" onClick={closePinModal}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-head">
                            <div className="modal-ico">
                                <Ico name="keypad" size={18} />
                            </div>
                            <div className="modal-title">
                                {t('employees.pin_modal_title', { name: pinModal.full_name })}
                            </div>
                            <button type="button" className="btn-icon modal-x" onClick={closePinModal} aria-label={t('common.cancel')}>
                                <Ico name="close" size={16} />
                            </button>
                        </div>
                        <form onSubmit={submitPin}>
                            <div className="field">
                                <label className="field-label" htmlFor="emp-pin">
                                    {t('employees.pin_field')}
                                </label>
                                <div className="control">
                                    <span className="control-ico"><Ico name="hash" size={17} /></span>
                                    <input
                                        id="emp-pin"
                                        className="input pad-ico mono"
                                        type="password"
                                        inputMode="numeric"
                                        autoComplete="new-password"
                                        pattern="\d*"
                                        maxLength={8}
                                        value={pinValue}
                                        onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 8))}
                                        placeholder="••••"
                                        autoFocus
                                    />
                                </div>
                                <div className="hint">{t('employees.pin_hint')}</div>
                            </div>

                            {pinError && (
                                <div className="alert alert-error" role="alert">
                                    <Ico name="alert" size={17} />
                                    <span>{pinError}</span>
                                </div>
                            )}

                            <div className="modal-actions">
                                {pinModal.pin_set && (
                                    <button type="button" onClick={removePin} disabled={pinSubmitting} className="btn btn-danger-ghost">
                                        <Ico name="trash" size={14} />
                                        {t('employees.pin_remove')}
                                    </button>
                                )}
                                <div className="modal-spacer" />
                                <button type="button" onClick={closePinModal} className="btn btn-ghost">
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" disabled={pinSubmitting} className="btn btn-primary">
                                    {pinSubmitting
                                        ? <><Ico name="refresh" size={15} />{t('common.saving')}</>
                                        : <><Ico name="check" size={15} />{t('employees.pin_submit')}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}

export default Employees;
